/**
 * data sample report page from 'data-key-set-list-page'
 * dateKeySetId is required
 */
import '@operato/data-grist'

import gql from 'graphql-tag'
import { css, html } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { connect } from 'pwa-helpers/connect-mixin'
import moment from '@operato/moment-timezone-es'

import { client } from '@operato/graphql'
import { i18next, localize } from '@operato/i18n'
import { PageView, store } from '@operato/shell'
import { ScrollbarStyles } from '@operato/styles'
import { encodeUrlParams } from '@operato/utils'
import { FetchOption } from '@operato/data-grist'

@customElement('data-report-samples-page')
export class DataReportSamplesPage extends connect(store)(localize(i18next)(PageView)) {
  static styles = [
    ScrollbarStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        padding: 0;
      }

      #container {
        flex: 1;
        padding: 0;
        margin: 0;
        border: 0;
      }
    `
  ]

  @state() dataKeySetId?: string
  @state() dataKeySet: any
  @query('iframe') container!: HTMLIFrameElement

  get context() {
    const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD')
    const today = moment().format('YYYY-MM-DD')

    const filters = [
      {
        name: 'workDateRange',
        type: 'date',
        label: i18next.t('field.work-date'),
        operator: 'between',
        value: [yesterday, today]
      },
      ...this.getDataKeyFilters()
    ]

    return {
      title: i18next.t('title.data-report samples'),
      help: 'dataset/data-report-samples',
      filters
    }
  }

  getDataKeyFilters() {
    return (
      this.dataKeySet?.dataKeyItems.map((item, index) => {
        return {
          type: 'string',
          name: `key_0${index + 1}`,
          label: i18next.t(item.tKey),
          operator: 'i_like',
          width: 120
        }
      }) || []
    )
  }

  render() {
    // clear if pre-loaded
    this.container.src = ''

    return html`
      <ox-filters-form-base
        .filters=${this.context.filters}
        @filters-change=${e => {
          console.log('filters changed', e.detail)
          this.renderTemplate(e.detail.filters)
        }}
        ?url-params-sensitive=${this.active}
      ></ox-filters-form-base>

      <iframe></iframe>
    `
  }

  renderTemplate(filters) {
    const params = filters.reduce((acc, curr) => ((acc[curr.name] = curr.value), acc), {})
    Object.assign(params, this.lifecycle.params)

    const filterNames = this.getDataKeyFilters().map(x => x.name)
    const dataKeyFilters = filters.filter(x => filterNames.includes(x.name))
    const { workDateRange } = params

    /** ignoring date conditions  */
    if (!workDateRange[0] || !workDateRange[1] || workDateRange[0] > workDateRange[1]) {
      return
    }

    /** urlencoded params including test values */
    const urlParams = {
      reportType: params.reportType,
      reportView: params.reportView,
      reportTemplate: params.reportTemplate,
      table: 'samples',
      datasetId: params.datasetId,
      dataKeySetId: params.dataKeySetId,
      startDate: workDateRange[0],
      endDate: workDateRange[1],
      dataKeySetItems: JSON.stringify(this.dataKeySet?.dataKeyItems || []),
      dataKeyFilters: JSON.stringify(dataKeyFilters)
    }

    const encodedUrlParams = encodeUrlParams(urlParams)

    const reportUrl = `/data-report/${urlParams.reportType}/`
    this.container.src = `${reportUrl}?${encodedUrlParams}`
  }

  pageUpdated(changes, lifecycle) {
    if (this.active) {
      const { dataKeySetId } = lifecycle.params
      this.dataKeySetId = dataKeySetId
      return
    }
  }

  async fetchHandler({ page, limit, sortings = [], filters = [] }: FetchOption) {}

  async updated(changes) {
    if (changes.has('dataKeySetId')) {
      if (!this.dataKeySetId) {
        this.dataKeySet = null
        return
      }

      const response = await client.query({
        query: gql`
          query ($id: String!) {
            dataKeySet(id: $id) {
              id
              name
              description
              reportType
              reportView
              dataKeyItems {
                name
                description
                dataKey
                tKey
              }
            }
          }
        `,
        variables: {
          id: this.dataKeySetId
        }
      })

      this.dataKeySet = response.data.dataKeySet
    }
  }
}
